import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Radio, Eye, EyeOff, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/opportunities';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please enter email and password');
            return;
        }

        setLoading(true);
        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate(from, { replace: true });
        } catch (error) {
            toast.error(error.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Noise overlay */}
            <div className="noise-overlay" />

            {/* Left panel - Image */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1760895223972-57b1d858d77e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHN0dWRpbyUyMHByb2R1Y3Rpb24lMjBkYXJrJTIwcmVkJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY5NjYwODg2fDA&ixlib=rb-4.1.0&q=85')`
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                
                {/* Content overlay */}
                <div className="relative z-10 p-12 flex flex-col justify-end">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F51E1E]/20 border border-[#F51E1E]/30 mb-6">
                            <span className="w-2 h-2 rounded-full bg-[#F51E1E] animate-pulse"></span>
                            <span className="text-sm text-[#F51E1E] font-medium">ON AIR</span>
                        </div>
                        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                            Your music.<br />Their screens.
                        </h2>
                        <p className="text-lg text-[#A3A3A3] max-w-md">
                            Access exclusive sync licensing opportunities and get your tracks placed in films, TV shows, and commercials.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Right panel - Login form */}
            <div className="w-full lg:w-1/2 bg-[#121212] flex items-center justify-center p-8">
                <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="w-full max-w-md"
                >
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-12 h-12 rounded-xl bg-[#F51E1E] flex items-center justify-center">
                            <Radio size={26} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white tracking-tight">Pitch Room</h1>
                            <p className="text-sm text-[#A3A3A3]">Sync Portal</p>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-white mb-2">Welcome back</h2>
                        <p className="text-[#A3A3A3]">Sign in to access your sync opportunities</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="email-input"
                                className="h-12 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500 focus:border-[#F51E1E] focus:ring-[#F51E1E]/20"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="password-input"
                                    className="h-12 bg-[#1a1a1a] border-[#404040] text-white placeholder:text-gray-500 focus:border-[#F51E1E] focus:ring-[#F51E1E]/20 pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-btn"
                            className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white rounded-full font-semibold uppercase tracking-wide transition-colors"
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" size={20} />
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 space-y-4">
                        <p className="text-center text-sm text-[#A3A3A3]">
                            This is an invite-only platform. Contact your administrator for access.
                        </p>
                        
                        <div className="border-t border-[#333] pt-4">
                            <p className="text-center text-sm text-[#A3A3A3]">
                                Looking to sync music for your project?
                            </p>
                            <a 
                                href="/poster-signup" 
                                className="block text-center text-[#F51E1E] hover:underline text-sm mt-1 font-medium"
                            >
                                Sign up to post projects →
                            </a>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
